import React from "react";
import { finApi, money } from "@/lib/financeApi";
import { Chip, Empty, ErrorBox, Loading, useLoad, when } from "@/components/nds/windows/finance/parts";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";

const useHistory = (vendorId) => useLoad(() => finApi.get(`/vendors/${vendorId}/landlord-history`), [vendorId]);
const th = "px-3 py-2 font-normal";
const Stat = ({ label, value, testid, tone = "text-nex-silver" }) => (
  <div className="p-3 border border-white/10 rounded-sm bg-white/[0.02]">
    <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">{label}</div>
    <div data-testid={testid} className={`mt-1 font-mono text-lg ${tone}`}>{money(value)}</div>
  </div>
);

function Guard({ state, load, testid, children }) {
  if (state.loading && !state.data) return <Loading testid={`${testid}-loading`} />;
  if (state.error) return <ErrorBox testid={`${testid}-error`} error={state.error} onRetry={load} />;
  return children(state.data);
}

export function LandlordEscrowTab({ vendor }) {
  const [state, load] = useHistory(vendor.id);
  return (
    <Guard state={state} load={load} testid="fin-landlord-escrow">
      {(d) => (
        <div data-testid="fin-landlord-escrow-tab" className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Rent held (in)" value={d.escrow_in} testid="fin-landlord-escrow-in" />
            <Stat label="Paid to landlord (out)" value={d.escrow_out} testid="fin-landlord-escrow-out" />
            <Stat label="Escrow balance" value={d.escrow_balance} testid="fin-landlord-escrow-balance" tone="text-nex-gold" />
          </div>
          <p className="text-[11px] text-nex-muted">Imported from the old platform · landlord history only. Residents' Escrow balances are not changed by these entries.</p>
          {d.escrow.length ? (
            <div className="border border-white/10 rounded-sm overflow-x-auto">
              <table data-testid="fin-landlord-escrow-table" className="w-full text-left">
                <thead><tr className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted">
                  {["Date", "Resident", "Employer / source", "Cycle", "In / Out", "Amount", "Balance"].map((h, i) => <th key={h} className={`${th} ${i > 4 ? "text-right" : ""}`}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {d.escrow.map((e) => (
                    <tr key={e.id} data-testid={`fin-landlord-escrow-row-${e.id}`} className="border-t border-white/5">
                      <td className="px-3 py-2 font-mono text-[11px] text-nex-muted whitespace-nowrap">{when(e.occurred_at)}</td>
                      <td className="px-3 py-2 text-sm text-nex-silver"><span className="inline-flex items-center gap-2">{e.resident_name || "—"}<OpenResidentButton residentId={e.resident_id} name={e.resident_name} testid={`open-resident-escrow-${e.id}`} /></span></td>
                      <td className="px-3 py-2 text-xs text-nex-muted">{e.employer_name || e.notes || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-nex-muted">{e.cycle}</td>
                      <td className="px-3 py-2"><Chip tone={e.kind === "credit" ? "green" : "orange"}>{e.kind === "credit" ? "Held" : "Paid out"}</Chip></td>
                      <td className="px-3 py-2 font-mono text-xs text-right text-nex-silver">{money(e.amount)}</td>
                      <td className="px-3 py-2 font-mono text-xs text-right text-nex-gold">{money(e.balance_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty testid="fin-landlord-escrow-empty">No escrow history for this landlord.</Empty>}
        </div>
      )}
    </Guard>
  );
}

export function LandlordReimbursablesTab({ vendor }) {
  const [state, load] = useHistory(vendor.id);
  return (
    <Guard state={state} load={load} testid="fin-landlord-reimb">
      {(d) => (
        <div data-testid="fin-landlord-reimb-tab" className="p-5 space-y-4">
          <Stat label="Reimbursables total" value={d.reimbursable_total} testid="fin-landlord-reimb-total" tone="text-nex-gold" />
          {d.reimbursables.length ? (
            <ul data-testid="fin-landlord-reimb-list" className="divide-y divide-white/5 border border-white/10 rounded-sm">
              {d.reimbursables.map((r) => (
                <li key={r.id} data-testid={`fin-landlord-reimb-row-${r.id}`} className="px-4 py-3 flex items-center gap-3">
                  <span className="font-mono text-[11px] text-nex-muted w-24">{r.occurred_at}</span>
                  <span className="flex-1 text-sm text-nex-silver">{r.description}</span>
                  <Chip tone={r.status === "applied" ? "green" : "yellow"}>{r.status === "applied" ? "Applied" : "Pending"}</Chip>
                  <span className="font-mono text-xs text-nex-silver w-20 text-right">{money(r.amount)}</span>
                </li>
              ))}
            </ul>
          ) : <Empty testid="fin-landlord-reimb-empty">No reimbursables for this landlord.</Empty>}
        </div>
      )}
    </Guard>
  );
}
